import { useState } from 'react';
import { Card } from '../ui';
import { CardTitle, EmptyNote, MoreButton, extraColors } from './common';

function chipColors(t, c, chip) {
  switch (chip) {
    case 'progressing': return { color: t.accent, background: t.accentSoft };
    case 'stalled': return { color: c.amber, background: c.amberSoft };
    case 'holding': return { color: c.blue, background: c.blueSoft };
    case 'regressing': return { color: t.danger, background: t.dangerSoft };
    default: return { color: t.textFaint, background: t.badgeBg };
  }
}

export function StatusChip({ t, c, chip }) {
  return (
    <span style={{
      ...chipColors(t, c, chip), flexShrink: 0, borderRadius: 999, padding: '3px 8px',
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>{chip}</span>
  );
}

/* One row per lift trained in the last 8 weeks with its trend status and the
 * sentence from logic/trends liftTrend (the same words Claude uses). Lifts
 * without enough clean sessions are folded away so the flags stand out. */
export function LiftStatusCard({ t, isDark, rows, selected, onSelect }) {
  const [showRest, setShowRest] = useState(false);
  const c = extraColors(isDark);
  const judged = rows.filter(r => r.trend.status !== 'insufficient');
  const rest = rows.filter(r => r.trend.status === 'insufficient');
  const shown = showRest ? [...judged, ...rest] : judged;
  const count = chip => judged.filter(r => r.chip === chip).length;
  const summary = ['progressing', 'stalled', 'holding', 'regressing']
    .map(chip => ({ chip, n: count(chip) }))
    .filter(x => x.n > 0);

  return (
    <Card t={t} padding={16}>
      <CardTitle t={t} sub="Lifts trained in the last 8 weeks, judged on the best set's e1RM per session. A new program block restarts the trend.">
        Lift status
      </CardTitle>

      {rows.length === 0 ? (
        <EmptyNote t={t}>Log a few sessions to see how each lift is trending.</EmptyNote>
      ) : (
        <>
          {summary.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {summary.map(x => (
                <span key={x.chip} style={{ fontSize: 12, color: t.textMuted, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <b style={{ color: chipColors(t, c, x.chip).color }}>{x.n}</b> {x.chip}
                </span>
              ))}
            </div>
          )}
          {judged.length === 0 && !showRest && (
            <div style={{ fontSize: 12, color: t.textMuted, padding: '6px 0' }}>
              No lift has 6 clean sessions in the last 12 weeks yet.
            </div>
          )}
          <div>
            {shown.map(r => {
              const active = r.name === selected;
              return (
                <button key={r.name} type="button" onClick={() => onSelect(r.name)} style={{
                  display: 'block', width: '100%', textAlign: 'left', minHeight: 44, cursor: 'pointer',
                  padding: '9px 8px', borderRadius: 8,
                  background: active ? t.surfaceAlt : 'transparent',
                  border: `1px solid ${active ? t.accentBorder : 'transparent'}`,
                  borderBottomColor: active ? t.accentBorder : t.divider,
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: t.text, lineHeight: 1.3 }}>
                      {r.name}
                      {r.program && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: t.textFaint }}>
                          {r.program.name}
                        </span>
                      )}
                    </span>
                    <StatusChip t={t} c={c} chip={r.chip} />
                  </span>
                  <span style={{ display: 'block', fontSize: 12, color: t.textMuted, lineHeight: 1.45, marginTop: 3 }}>
                    {r.text}
                  </span>
                </button>
              );
            })}
          </div>
          {rest.length > 0 && (
            <MoreButton t={t} onClick={() => setShowRest(v => !v)}>
              {showRest ? 'Hide' : 'Show'} {rest.length} without enough data
            </MoreButton>
          )}
        </>
      )}
    </Card>
  );
}
